// Fenêtre « Chauffeur indisponible » (transport, lot T5b), ouverte depuis la page Dispatch (Patron et dispatcheurs) :
// journée(s) entière(s) ou créneau, motif court (jamais montré au chauffeur). Si le chauffeur a des courses sur ce
// créneau, le serveur répond 409 avec leur liste : la fenêtre la montre (« à réattribuer », « déjà commencée,
// laissée à … ») et l'on confirme ou l'on revient au formulaire. Modifier une indisponibilité : mêmes champs, le
// chauffeur ne change pas ; seules les courses de la partie ajoutée au créneau sont listées.
import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { todayIn } from "@/lib/helpers";
import { firstDay, lastDay } from "@/lib/unavailability";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const REASON_MAX = 60; // même limite que le serveur (metier.UNAV_REASON_MAX)

// Date et heure ISO → « AAAA-MM-JJTHH:MM » (heure de l'ordinateur, comme la fenêtre de course).
const toLocalInput = (iso) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
// Créneau proposé : aujourd'hui, de l'heure pleine suivante à 2 h plus tard ; un autre jour, de 09:00 à 11:00.
const defaultSlot = (day, tz) => {
  if (day === todayIn(tz)) {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    return [toLocalInput(d.toISOString()), toLocalInput(new Date(d.getTime() + 2 * 3600000).toISOString())];
  }
  return [`${day}T09:00`, `${day}T11:00`];
};

export default function UnavailabilityDialog({ open, onOpenChange, drivers, driverId, edit, day, tz, onSaved }) {
  const [form, setForm] = useState(null);
  const [hits, setHits] = useState(null); // réponse 409 du serveur : courses du créneau
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHits(null);
    if (edit) {
      setForm({ employee_id: edit.employee_id, all_day: !!edit.all_day, from_day: firstDay(edit, tz), to_day: lastDay(edit, tz),
        from: toLocalInput(edit.start), to: toLocalInput(edit.end), reason: edit.reason || "" });
    } else {
      const [from, to] = defaultSlot(day, tz);
      setForm({ employee_id: driverId || "", all_day: true, from_day: day, to_day: day, from, to, reason: "" });
    }
  }, [open, edit, driverId, day, tz]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const nom = drivers.find((d) => d.id === form.employee_id)?.name || edit?.employee_name || "ce chauffeur";
  const hour = (iso) => new Intl.DateTimeFormat("fr-FR", { timeZone: tz, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

  const envoyer = async (confirm) => {
    if (!form.employee_id) { toast.error("Choisissez le chauffeur"); return; }
    if (form.all_day ? !(form.from_day && form.to_day) : !(form.from && form.to)) { toast.error("Indiquez le début et la fin"); return; }
    if (form.all_day ? form.to_day < form.from_day : new Date(form.to) <= new Date(form.from)) {
      toast.error(form.all_day ? "Le dernier jour doit suivre le premier." : "La fin doit suivre le début."); return;
    }
    const corps = {
      start: form.all_day ? form.from_day : new Date(form.from).toISOString(),
      end: form.all_day ? form.to_day : new Date(form.to).toISOString(),
      all_day: form.all_day, reason: form.reason.trim(), confirm,
    };
    setSaving(true);
    try {
      const r = edit ? await api.patch(`/unavailabilities/${edit.id}`, corps) : await api.post("/unavailabilities", { ...corps, employee_id: form.employee_id });
      const n = r.data.released.length;
      toast.success(`Indisponibilité enregistrée${n ? ` · ${n} course${n > 1 ? "s" : ""} à réattribuer` : ""}`);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "rides_on_slot") setHits(d);
      else toast.error(apiError(d));
    } finally { setSaving(false); }
  };

  const ligne = (m) => (
    <li key={m.id} className="text-sm" data-testid={`unav-hit-${m.id}`}>
      <span className="tabular-nums">{hour(m.start)} → {hour(m.end).slice(-5)}</span>{m.route ? ` · ${m.route}` : ""}
      {m.step_label && m.step ? <span className="text-muted-foreground"> · {m.step_label}</span> : null}
    </li>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="unav-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{hits ? "Courses sur ce créneau" : edit ? "Modifier l'indisponibilité" : "Chauffeur indisponible"}</DialogTitle>
          {!hits && <DialogDescription>Le chauffeur voit ses indisponibilités dans « Mon planning », sans le motif.</DialogDescription>}
        </DialogHeader>

        {hits ? (
          <div className="space-y-3 min-w-0" data-testid="unav-hits">
            <p className="text-sm font-medium" data-testid="unav-hits-message">{hits.message}</p>
            {hits.rides.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm">{hits.rides.length > 1 ? "Elles seront à réattribuer :" : "Elle sera à réattribuer :"}</p>
                <ul className="list-disc pl-5 space-y-0.5 break-words">{hits.rides.map(ligne)}</ul>
              </div>
            )}
            {hits.started.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm">{`Déjà commencée${hits.started.length > 1 ? "s" : ""}, laissée${hits.started.length > 1 ? "s" : ""} à ${nom} :`}</p>
                <ul className="list-disc pl-5 space-y-0.5 break-words">{hits.started.map(ligne)}</ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 min-w-0">
            <div className="space-y-2 min-w-0"><Label>Chauffeur</Label>
              <Select value={form.employee_id} onValueChange={(v) => set("employee_id", v)} disabled={!!edit}>
                <SelectTrigger className="min-w-0 [&>span]:truncate [&>span]:block" data-testid="unav-driver"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{(edit && !drivers.some((d) => d.id === edit.employee_id) ? [{ id: edit.employee_id, name: edit.employee_name }, ...drivers] : drivers)
                  .map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-muted" role="radiogroup" aria-label="Durée">
              {[[true, "Journée(s) entière(s)"], [false, "Créneau"]].map(([v, l]) => (
                <button key={l} type="button" role="radio" aria-checked={form.all_day === v} onClick={() => set("all_day", v)} data-testid={`unav-kind-${v ? "days" : "slot"}`}
                  className={cn("rounded-full py-2 text-sm font-medium transition-colors", form.all_day === v ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {form.all_day ? <>
                <div className="space-y-2 min-w-0"><Label>Premier jour</Label>
                  <Input type="date" value={form.from_day} onChange={(e) => setForm((f) => ({ ...f, from_day: e.target.value, to_day: f.to_day < e.target.value ? e.target.value : f.to_day }))} data-testid="unav-from-day" /></div>
                <div className="space-y-2 min-w-0"><Label>Dernier jour</Label>
                  <Input type="date" value={form.to_day} min={form.from_day} onChange={(e) => set("to_day", e.target.value)} data-testid="unav-to-day" /></div>
              </> : <>
                <div className="space-y-2 min-w-0"><Label>Du</Label>
                  <Input type="datetime-local" value={form.from} onChange={(e) => set("from", e.target.value)} data-testid="unav-from" /></div>
                <div className="space-y-2 min-w-0"><Label>Au</Label>
                  <Input type="datetime-local" value={form.to} onChange={(e) => set("to", e.target.value)} data-testid="unav-to" /></div>
              </>}
            </div>
            <div className="space-y-2 min-w-0"><Label>Motif <span className="font-normal text-muted-foreground">(facultatif, jamais montré au chauffeur)</span></Label>
              <Input value={form.reason} maxLength={REASON_MAX} onChange={(e) => set("reason", e.target.value)} placeholder="Ex. congés, médecin" data-testid="unav-reason" /></div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {hits ? <>
            <Button variant="outline" className="rounded-full" onClick={() => setHits(null)} data-testid="unav-back">Retour</Button>
            <Button className="rounded-full" onClick={() => envoyer(true)} disabled={saving} data-testid="unav-confirm">
              {hits.rides.length === 0 ? "Enregistrer quand même" : hits.rides.length > 1 ? "Enregistrer et libérer ces courses" : "Enregistrer et libérer cette course"}
            </Button>
          </> : <>
            <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button className="rounded-full" onClick={() => envoyer(false)} disabled={saving} data-testid="unav-save">Enregistrer</Button>
          </>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
